import { useNavigate } from 'react-router-dom';
import { useContext } from 'react';
import { AuthContext } from '../../../hooks/useAuth'; // ← À adapter selon ton chemin

interface QuickAction {
  title: string;
  description: string;
  icon: string;
  color: string;
  path: string;
  requiredPermissions?: string[];     // Clés du Record (ex: 'sales', 'inventory')
  requiredRoleName?: string;          // Nom du rôle (ex: 'Administrateur')
}

const ALL_QUICK_ACTIONS: QuickAction[] = [
  {
    title: 'Nouvelle Vente',
    description: 'Créer une nouvelle facture',
    icon: 'ri-add-circle-line',
    color: 'bg-green-500 hover:bg-green-600',
    path: '/sales',
    requiredPermissions: ['sales']
  },
  {
    title: 'Commande Fournisseur',
    description: 'Passer une commande',
    icon: 'ri-shopping-bag-line',
    color: 'bg-blue-500 hover:bg-blue-600',
    path: '/purchase-orders',
    requiredPermissions: ['purchase_orders']
  },
  {
    title: 'Nouveau Client',
    description: 'Ajouter un client',
    icon: 'ri-user-add-line',
    color: 'bg-purple-500 hover:bg-purple-600',
    path: '/customers',
    requiredPermissions: ['customers']
  },
  {
    title: 'Mouvement Stock',
    description: 'Ajuster le stock',
    icon: 'ri-exchange-line',
    color: 'bg-orange-500 hover:bg-orange-600',
    path: '/products',
    requiredPermissions: ['inventory']
  },
  {
    title: 'Encaissement',
    description: 'Enregistrer un paiement',
    icon: 'ri-money-dollar-box-line',
    color: 'bg-teal-500 hover:bg-teal-600',
    path: '/cash-register',
    requiredPermissions: ['cash_register']
  },
  {
    title: 'Rapport Ventes',
    description: 'Générer un rapport',
    icon: 'ri-file-chart-line',
    color: 'bg-indigo-500 hover:bg-indigo-600',
    path: '/reports',
    requiredPermissions: ['reports']
  },
  {
    title: 'Caisse',
    description: 'Ouvrir la caisse',
    icon: 'ri-safe-line',
    color: 'bg-yellow-500 hover:bg-yellow-600',
    path: '/cash-register',
    requiredPermissions: ['cash_register']
  },
  {
    title: 'Gestion Admin',
    description: 'Paramètres système',
    icon: 'ri-settings-line',
    color: 'bg-red-500 hover:bg-red-600',
    path: '/admin',
    requiredRoleName: 'Administrateur'
  },
];

export default function QuickActions() {
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);

  // Vérifie si l'utilisateur a une permission (au moins 'read' ou 'create')
  const hasPermission = (key: string): boolean => {
    return user?.permissions?.[key] 
      ? user.permissions[key].read || 
        user.permissions[key].create || 
        user.permissions[key].update || 
        user.permissions[key].delete || 
        user.permissions[key].validate
      : false;
  };

  // Vérifie si l'utilisateur a le rôle requis
  const hasRole = (roleName: string): boolean => {
    return user?.role?.nom === roleName;
  };

  // Superadmin a tous les droits
  const isSuperAdmin = user?.est_superadmin === true;

  // Détermine si l'utilisateur a accès à une action
  const hasAccess = (action: QuickAction): boolean => {
    if (isSuperAdmin) return true;

    if (action.requiredRoleName) {
      return hasRole(action.requiredRoleName);
    }

    if (action.requiredPermissions) {
      return action.requiredPermissions.some(perm => hasPermission(perm));
    }

    return true;
  };

  const availableActions = ALL_QUICK_ACTIONS.filter(hasAccess);

  const handleNavigation = (path: string) => {
    navigate(path);
  };

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200">
      <div className="p-6 border-b border-gray-200">
        <h3 className="text-lg font-semibold text-gray-900">Actions Rapides</h3>
      </div>

      <div className="p-6">
        {availableActions.length > 0 ? (
          <div className="grid grid-cols-1 gap-3">
            {availableActions.map((action) => (
              <button
                key={action.path}
                onClick={() => handleNavigation(action.path)}
                className={`${action.color} text-white p-4 rounded-lg text-left transition-all duration-200 group flex items-center space-x-3 shadow-sm hover:shadow`}
                aria-label={action.title}
              >
                <i className={`${action.icon} text-xl`} aria-hidden="true"></i>
                <div>
                  <p className="font-medium text-sm">{action.title}</p>
                  <p className="text-xs opacity-90">{action.description}</p>
                </div>
                <i className="ri-arrow-right-line ml-auto opacity-70 group-hover:opacity-100"></i>
              </button>
            ))}
          </div>
        ) : (
          <div className="text-center py-12 text-gray-500">
            <i className="ri-lock-line text-4xl mb-4"></i>
            <p className="text-sm font-medium">Aucune action disponible</p>
            <p className="text-xs mt-1">Contactez votre administrateur pour plus de droits.</p>
          </div>
        )}
      </div>

      <div className="p-6 border-t border-gray-200 bg-gray-50 rounded-b-xl">
        <div className="text-center">
          <p className="text-sm text-gray-600 mb-3">Besoin d&apos;aide ?</p>
          <button
            className="text-sm text-blue-600 hover:text-blue-700 font-medium inline-flex items-center"
            onClick={() => handleNavigation('/guide')}
          >
            <i className="ri-question-line mr-1"></i>
            Guide d&apos;utilisation
          </button>
        </div>
      </div>
    </div>
  );
}